package no.vareflow.web

import no.vareflow.app.domain.*

fun WebApp.renderProducts(root: El, user: UserAccount) {
    val categories = core.orderedCategories().filter { AccessRules.canViewCategory(user, it) }
    if (productsCategory != null && productsCategory !in categories) productsCategory = null
    val canSeePrice = AccessRules.hasPermission(user, Permission.PRODUCT_VIEW_PRICE)
    val isAdmin = user.role == Role.ADMIN
    val category = productsCategory

    if (category == null) {
        root.pageHeader(t("Produkter", "Products"), t("Hovedvarer og undervarer", "Main categories and sub-items")) {
            if (isAdmin) button(t("Importer fra Excel", "Import from Excel"), "btn outline") { showProductImport(user) }
            if (isAdmin && categories.size >= 2) button(t("Slå sammen", "Merge"), "btn outline") { showMergeCategories(user) }
            if (isAdmin) button(t("+ Ny hovedvare", "+ New main category"), "btn tonal") {
                nameDialog(t("Ny hovedvare", "New main category"), "") { name ->
                    val ok = core.addCategory(name, user)
                    if (ok) toast(t("$name er opprettet.", "$name has been created."))
                    ok
                }
            }
        }
        root.div("search") {
            input(productsSearch, "products-search", "search-input", t("Søk i alle produkter", "Search all products"), "search") {
                productsSearch = it
                requestRender()
            }.attr("aria-label", t("Søk i alle produkter", "Search all products"))
        }
        val query = productsSearch.trim().lowercase()
        if (query.isNotEmpty()) {
            val hits = core.products.filter { it.category in categories }.filter { p ->
                p.name.lowercase().contains(query) || p.varenummer.lowercase().contains(query) || p.supplier.lowercase().contains(query)
            }.sortedWith { a, b -> compareNorwegian(a.name, b.name) }
            if (hits.isEmpty()) root.emptyCard(t("Ingen produkter matcher søket.", "No products match the search."))
            else root.div("list") { hits.forEach { productRow(this@renderProducts, it, user, canSeePrice, showCategory = true) } }
            return
        }
        if (categories.isEmpty() && isAdmin) root.div("card") {
            div("card-title", t("Ingen varer ennå", "No products yet"))
            p(t("Importer varelisten fra Excel (.xlsx) eller CSV, eller lag hovedvarer og varer for hånd.", "Import the product list from Excel (.xlsx) or CSV, or create main categories and products by hand."), "muted small")
            button(t("Importer fra Excel", "Import from Excel"), "btn primary") { showProductImport(user) }
        }
        root.div("card-grid") {
            categories.forEach { name ->
                val count = core.products.count { it.category == name }
                el("button", "card nav-card") {
                    attr("type", "button")
                    div("grow") {
                        div("card-title", name)
                        div("muted small", if (count == 1) t("1 undervare", "1 sub-item") else t("$count undervarer", "$count sub-items"))
                        uploadProgressLine(cloud.uploadProgress(name))
                    }
                    span("chevron", "›").attr("aria-hidden", "true")
                    onClick { productsCategory = name; window.scrollTo(0, 0); requestRender() }
                }
            }
        }
        return
    }

    root.button(t("‹ Hovedvarer", "‹ Main categories"), "btn text back") { productsCategory = null; requestRender() }
    root.pageHeader(category, t("Undervarer", "Sub-items")) {
        if (isAdmin) {
            button(t("Rediger", "Edit"), "btn outline") { manageCategory(category, user) { productsCategory = it } }
            button(t("+ Ny undervare", "+ New sub-item"), "btn tonal") { productForm(null, category, user) }
        }
    }
    val products = core.orderedProducts(category)
    if (products.isEmpty()) {
        root.emptyCard(t("Ingen undervarer i $category ennå.", "No sub-items in $category yet."))
        return
    }
    root.div("list") {
        groupByUndergruppe(products).forEach { (undergruppe, group) ->
            if (undergruppe.isNotEmpty()) div("subgroup-title", undergruppe).attr("role", "heading").attr("aria-level", "3")
            group.forEach { productRow(this@renderProducts, it, user, canSeePrice, showCategory = false) }
        }
    }
}

private fun El.productRow(app: WebApp, product: Product, user: UserAccount, canSeePrice: Boolean, showCategory: Boolean) {
    // Field-level product rights (phone: ProductEditDialog) come with the cloud step; admin edits here.
    val canEdit = user.role == Role.ADMIN
    el("button", "card product-row" + if (product.active) "" else " inactive") {
        attr("type", "button")
        div("grow") {
            div("strong", product.name)
            div("muted small", buildString {
                if (product.varenummer.isNotBlank()) append("${product.varenummer} · ")
                if (showCategory) append(product.category + (if (product.undergruppe.isNotBlank()) " · ${product.undergruppe}" else "") + " · ")
                append(product.unit)
                if (canSeePrice) append(" · ${formatPrice(product.pricePerUnit)} kr/${product.unit}")
                if (!product.active) append(t(" · INAKTIV", " · INACTIVE"))
            })
            if (product.importStatus.equals("Kontroller", true)) div("warn-text tiny", product.dataQuality.ifBlank { t("Kontroller data", "Check data") })
        }
        span("chevron", "›").attr("aria-hidden", "true")
        onClick {
            app.showProductDetails(product, canSeePrice, if (canEdit) ({ app.productForm(product, product.category, user) }) else null)
        }
    }
}

/** New or edited product (admin, or users with product field permissions). */
fun WebApp.productForm(existing: Product?, category: String, user: UserAccount) {
    val canPrice = user.role == Role.ADMIN || AccessRules.hasPermission(user, Permission.PRODUCT_VIEW_PRICE)
    val state = object {
        var name = existing?.name.orEmpty()
        var varenummer = existing?.varenummer.orEmpty()
        var unit = existing?.unit ?: "stk"
        var price = existing?.pricePerUnit?.let(::formatQuantity).orEmpty()
        var vat = formatQuantity(existing?.vat ?: 15.0)
        var supplier = existing?.supplier.orEmpty()
        var aliases = existing?.aliases?.joinToString(", ").orEmpty()
        var undergruppe = existing?.undergruppe.orEmpty()
        /** "+ Ny undergruppe …" chosen: type a new name. */
        var newUndergruppe = false
        var cat = existing?.category ?: category
        var active = existing?.active ?: true
        var approve = false
        var error: String? = null
    }
    openDialog(if (existing == null) t("Ny undervare", "New sub-item") else t("Rediger undervare", "Edit sub-item")) { close ->
        div("form-grid") {
            field(t("Varenavn", "Product name"), state.name, "pf-name") { state.name = it }
            field(t("Varenummer", "Item number"), state.varenummer, "pf-nr") { state.varenummer = it }
            div("field") {
                span("field-label", t("Hovedvare", "Main category"))
                selectBox(this@productForm, "pf-cat", core.orderedCategories().map { it to it }, state.cat, t("Hovedvare", "Main category")) { state.cat = it }
            }
            div("field") {
                span("field-label", t("Undergruppe", "Subgroup"))
                val options = (undergrupperIn(core.products, state.cat) + listOfNotNull(state.undergruppe.takeIf { it.isNotBlank() }))
                    .distinctBy { it.lowercase() }
                val selectedUg = when {
                    state.newUndergruppe -> "__new__"
                    state.undergruppe.isBlank() -> ""
                    else -> options.firstOrNull { it.equals(state.undergruppe, true) } ?: state.undergruppe
                }
                selectBox(this@productForm, "pf-ug",
                    listOf("" to t("— ingen —", "— none —")) + options.map { it to it } + ("__new__" to t("+ Ny undergruppe …", "+ New subgroup …")),
                    selectedUg, t("Undergruppe", "Subgroup")) { v ->
                    if (v == "__new__") { state.newUndergruppe = true; state.undergruppe = "" } else { state.newUndergruppe = false; state.undergruppe = v }
                }
            }
            if (state.newUndergruppe) field(t("Ny undergruppe", "New subgroup"), state.undergruppe, "pf-ug-new", placeholder = t("f.eks. Rødvin", "e.g. Red wine")) { state.undergruppe = it }
            field(t("Enhet", "Unit"), state.unit, "pf-unit") { state.unit = it }
            if (canPrice) field(t("Innkjøpspris eks. MVA (kr)", "Purchase price excl. VAT (NOK)"), state.price, "pf-price") { state.price = it }
            div("field") {
                span("field-label", t("MVA", "VAT"))
                selectBox(this@productForm, "pf-vat", listOf("15" to "15 %", "25" to "25 %"), state.vat, t("MVA", "VAT")) { state.vat = it }
            }
            field(t("Leverandør", "Supplier"), state.supplier, "pf-sup") { state.supplier = it }
            field(t("Aliaser (kommaseparert)", "Aliases (comma-separated)"), state.aliases, "pf-alias") { state.aliases = it }
        }
        checkbox(t("Aktiv", "Active"), state.active, "pf-active") { state.active = it }
        if (existing != null && existing.importStatus.equals("Kontroller", ignoreCase = true)) {
            div("quality-box") {
                div("strong small", t("Datakvalitet", "Data quality"))
                div("small", existing.dataQuality.ifBlank { t("Produktet krever menneskelig kontroll.", "The product needs a human check.") })
                if (canReviewQuality(user)) checkbox(t("Kontrollert – sett status til Klar", "Checked – set status to Ready"), state.approve, "pf-approve") { state.approve = it }
            }
        }
        errorText(state.error)
        dialogActions {
            if (existing != null && user.role == Role.ADMIN) {
                button(t("Slett", "Delete"), "btn text danger-text") {
                    openDialog(t("Slett ${existing.name}?", "Delete ${existing.name}?")) { closeConfirm ->
                        p(t("Undervaren slettes fra produktregisteret. Historiske snapshots beholdes.", "The sub-item is deleted from the product register. Historical snapshots are kept."))
                        dialogActions {
                            button(t("Avbryt", "Cancel"), "btn text") { closeConfirm() }
                            button(t("Slett", "Delete"), "btn danger") {
                                core.deleteProduct(existing.id, user)
                                closeConfirm()
                                toast(t("${existing.name} er slettet.", "${existing.name} has been deleted."))
                            }
                        }
                    }
                }
            }
            span("spacer")
            button(t("Avbryt", "Cancel"), "btn text") { close() }
            button(t("Lagre", "Save"), "btn primary") {
                val price = if (state.price.isBlank()) 0.0 else state.price.replace(',', '.').toDoubleOrNull()
                when {
                    state.name.isBlank() -> state.error = t("Varenavn må fylles ut.", "The product name is required.")
                    state.unit.isBlank() -> state.error = t("Enhet må fylles ut.", "The unit is required.")
                    price == null || price < 0 -> state.error = t("Pris må være et tall.", "The price must be a number.")
                    else -> {
                        val base = existing ?: Product(name = "", unit = "", pricePerUnit = 0.0, category = state.cat)
                        val product = base.copy(
                            name = state.name.trim(), varenummer = state.varenummer.trim(), unit = state.unit.trim(),
                            pricePerUnit = if (canPrice) price else base.pricePerUnit,
                            vat = normalizeProductVat(state.vat.toDoubleOrNull() ?: 15.0), supplier = state.supplier.trim(),
                            aliases = state.aliases.split(',').map { it.trim() }.filter { it.isNotEmpty() },
                            category = state.cat, active = state.active,
                            undergruppe = cleanUndergruppe(state.undergruppe),
                            importStatus = if (state.approve && canReviewQuality(user)) "Klar" else existing?.importStatus ?: "Manuell",
                            dataQuality = if (state.approve && canReviewQuality(user)) "" else base.dataQuality
                        )
                        if (existing == null) core.addProduct(product, user) else core.updateProduct(product, user)
                        close()
                        toast(t("${product.name} er lagret.", "${product.name} is saved."))
                        return@button
                    }
                }
                requestRender()
            }
        }
    }
}

/** "Laster opp … 42 %" with a thin bar under a hovedvare while its products go to the cloud. */
fun El.uploadProgressLine(progress: WebCloud.UploadProgress?) {
    if (progress == null) return
    div("upload-progress" + if (progress.finished) " done" else "") {
        attr("data-key", "upload-progress")
        attr("role", "progressbar")
        attr("aria-valuemin", "0"); attr("aria-valuemax", "100"); attr("aria-valuenow", progress.percent.toString())
        div("upload-progress-text",
            if (progress.finished) t("✓ Lastet opp", "✓ Uploaded")
            else t("Laster opp … ${progress.percent} %", "Uploading … ${progress.percent} %") + " (${progress.done}/${progress.total})")
        div("upload-progress-track") { div("upload-progress-fill").node.style.width = "${progress.percent}%" }
    }
}
